<!--
회원가입 폼 컴포넌트
--------------------------
기능:
    - 4개 섹션(계정/사업자정보/사업자등록증/센터선택) 카드 UI
    - 센터 선택 상태 관리 (카드형 체크박스)
    - 폼 제출 검증 (이름 필수, 센터 1곳 이상)
    - 파일 업로드 드래그앤드롭 스타일 UI

사용:
    <app-register-form
        action="/users"
        csrf-token="{{ csrf_token() }}"
        :fields='[...]'
        :agents='[...]'
        :reperral='"코드"'
    ></app-register-form>
-->
<template>
    <form method="post"
          :action="action"
          @submit.prevent="onSubmit"
          enctype="multipart/form-data"
          class="uk-form-horizontal"
    >
        <input type="hidden" name="_token" :value="csrfToken">

        <div class="uk-width-xxlarge@l uk-width-1-1 uk-align-center">

            <!-- 필수 입력 안내 -->
            <div class="required-notice">
                <i class="material-icons required-notice-icon">info_outline</i>
                <span><span class="required-star">*</span> 표시는 필수 입력 항목입니다</span>
            </div>

            <!-- 섹션 1: 계정 -->
            <div class="uk-card uk-card-default uk-margin-bottom section-card">
                <div class="section-header">
                    <div class="section-header-left">
                        <i class="material-icons section-icon">person</i>
                        <h3 class="uk-card-title uk-margin-remove">계정</h3>
                    </div>
                </div>
                <div class="uk-card-body">
                    <fieldset class="uk-fieldset">
                        <template v-for="field in accountFields">
                            <!-- 추천인코드: 쿠키값이 있으면 disabled + hidden -->
                            <template v-if="field.name === 'u_reperral'">
                                <app-elem-input
                                    v-if="!reperral"
                                    :key="field.name"
                                    class="uk-margin"
                                    :type="field.type"
                                    :name="field.name"
                                    :label="field.label"
                                    :placeholder="field.placeholder"
                                    :init-val="field.initVal"
                                    :init-error-msg="field.initError"
                                ></app-elem-input>
                                <div v-else :key="field.name + '-wrap'">
                                    <app-elem-input
                                        class="uk-margin"
                                        :type="field.type"
                                        :label="field.label"
                                        :init-val="reperral"
                                        :init-error-msg="field.initError"
                                        disabled
                                    ></app-elem-input>
                                    <input type="hidden" name="u_reperral" :value="reperral">
                                </div>
                            </template>

                            <!-- 일반 필드 -->
                            <app-elem-input
                                v-else
                                :key="field.name"
                                class="uk-margin"
                                :type="field.type"
                                :name="field.name"
                                :label="field.label"
                                :placeholder="field.placeholder"
                                :init-val="field.initVal"
                                :init-error-msg="field.initError"
                            >
                                <div v-if="field.slotText" slot="input-bottom" class="slot-hint">
                                    <i class="material-icons slot-hint-icon">chat</i>
                                    {{ field.slotText }}
                                </div>
                            </app-elem-input>
                        </template>
                    </fieldset>
                </div>
            </div>

            <!-- 섹션 2: 사업자정보 -->
            <div class="uk-card uk-card-default uk-margin-bottom section-card">
                <div class="section-header">
                    <div class="section-header-left">
                        <i class="material-icons section-icon">business</i>
                        <h3 class="uk-card-title uk-margin-remove">사업자정보</h3>
                    </div>
                </div>
                <div class="uk-card-body">
                    <fieldset class="uk-fieldset">
                        <div v-for="field in businessFields" :key="field.name">
                            <app-elem-input
                                class="uk-margin"
                                :type="field.type"
                                :name="field.name"
                                :label="field.label"
                                :placeholder="field.placeholder"
                                :init-val="field.initVal"
                                :init-error-msg="field.initError"
                            ></app-elem-input>
                            <template v-if="field.name === 'c_customs_unique_no'">
                                <div class="uk-text-danger">
                                    * [구매대행업자 등록부호]가 없으신 경우는 입력하지 마시기 바랍니다.
                                </div>
                                <div class="uk-text-muted">
                                    (직전연도 구매대행 수입물품의 총 물품가격이 10억원 이상인 경우, 관세청으로부터 [구매대행업자 등록부호]를 부여받으셔야 합니다.)
                                </div>
                            </template>
                        </div>
                    </fieldset>
                </div>
            </div>

            <!-- 섹션 3: 사업자등록증 사본 -->
            <div class="uk-card uk-card-default uk-margin-bottom section-card">
                <div class="section-header">
                    <div class="section-header-left">
                        <i class="material-icons section-icon">description</i>
                        <h3 class="uk-card-title uk-margin-remove">사업자등록증 사본</h3>
                    </div>
                </div>
                <div class="uk-card-body">
                    <fieldset class="uk-fieldset">
                        <div class="file-upload-area" :class="{ 'file-upload-area--has-file': fileName }">
                            <input type="file" name="file" @change="onFileChange" class="file-upload-input">
                            <div class="file-upload-content">
                                <i class="material-icons file-upload-icon">cloud_upload</i>
                                <p v-if="!fileName" class="file-upload-text">클릭하여 이미지 파일을 선택하세요</p>
                                <p v-else class="file-upload-text file-upload-text--selected">
                                    <i class="material-icons" style="font-size: 1rem; vertical-align: middle;">attach_file</i>
                                    {{ fileName }}
                                </p>
                            </div>
                        </div>
                    </fieldset>
                </div>
            </div>

            <!-- 섹션 4: 센터 사용신청 -->
            <div class="uk-card uk-card-default uk-margin-bottom section-card">
                <div class="section-header">
                    <div class="section-header-left">
                        <i class="material-icons section-icon">place</i>
                        <h3 class="uk-card-title uk-margin-remove">센터 사용신청</h3>
                    </div>
                    <span class="section-required-hint">
                        <i class="material-icons" style="font-size: 1.15rem; vertical-align: middle;">warning</i>
                        1곳 이상 선택 필수
                    </span>
                </div>
                <div class="uk-card-body">
                    <div class="agent-card-grid">
                        <label v-for="agent in agents" :key="agent.id"
                               class="agent-card"
                               :class="{ 'agent-card--selected': isAgentChecked(agent.id) }"
                        >
                            <input type="checkbox" name="agent_id[]" :value="agent.id" v-model="checkedAgents" class="agent-card-checkbox">
                            <span class="agent-card-check" :class="{ 'agent-card-check--active': isAgentChecked(agent.id) }">
                                <i v-if="isAgentChecked(agent.id)" class="material-icons agent-card-check-icon">check</i>
                            </span>
                            <img :src="agent.flag16" class="agent-card-flag">
                            <span class="agent-card-name">{{ agent.name }}</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- 가입 버튼 -->
            <div class="uk-text-center uk-margin-top uk-margin-large-bottom">
                <button type="submit"
                        :disabled="!hasAgentSelected"
                        :style="submitButtonStyle"
                >
                    <i class="material-icons" style="font-size: 1.6rem; vertical-align: middle;">how_to_reg</i>
                    가입 신청하기
                </button>
            </div>

        </div>
    </form>
</template>

<script>
export default {
    props: {
        action: { type: String, required: true },
        csrfToken: { type: String, required: true },
        fields: { type: Array, required: true },
        agents: { type: Array, required: true },
        reperral: { default: null },
    },

    data: function () {
        var checkedAgents = [];
        for (var i = 0; i < this.agents.length; i++) {
            if (this.agents[i].checked) {
                checkedAgents.push(this.agents[i].id);
            }
        }
        return {
            checkedAgents: checkedAgents,
            fileName: '',
        };
    },

    computed: {
        accountFields: function () {
            return this.fields.filter(function (f) { return f.section === 'account'; });
        },
        businessFields: function () {
            return this.fields.filter(function (f) { return f.section === 'business'; });
        },
        hasAgentSelected: function () {
            return this.checkedAgents.length > 0;
        },
        submitButtonStyle: function () {
            var active = this.hasAgentSelected;
            return {
                display: 'inline-block',
                minWidth: '360px',
                padding: '18px 48px',
                fontSize: '1.35rem',
                fontWeight: '600',
                letterSpacing: '0.04em',
                border: 'none',
                borderRadius: '12px',
                color: '#fff',
                background: active ? 'linear-gradient(135deg, #1e87f0, #0f6dd6)' : '#ccc',
                boxShadow: active ? '0 4px 14px rgba(30,135,240,0.35)' : 'none',
                cursor: active ? 'pointer' : 'not-allowed',
                transition: 'all 0.3s ease',
            };
        },
    },

    methods: {
        isAgentChecked: function (id) {
            return this.checkedAgents.indexOf(id) !== -1;
        },

        onFileChange: function (e) {
            var files = e.target.files;
            this.fileName = files.length > 0 ? files[0].name : '';
        },

        getInputValue: function (name) {
            var el = this.$el.querySelector('[name="' + name + '"]');
            return el ? el.value.trim() : '';
        },

        focusInput: function (name) {
            var el = this.$el.querySelector('[name="' + name + '"]');
            if (el) el.focus();
        },

        onSubmit: function () {
            // 이메일 필수
            if (this.getInputValue('u_email') === '') {
                alert('이메일을 입력해 주세요.');
                this.focusInput('u_email');
                return;
            }

            // 비밀번호 필수
            if (this.getInputValue('u_password') === '') {
                alert('비밀번호를 입력해 주세요.');
                this.focusInput('u_password');
                return;
            }

            // 비밀번호 확인 일치
            if (this.getInputValue('u_password') !== this.getInputValue('u_password_confirmation')) {
                alert('비밀번호가 일치하지 않습니다.');
                this.focusInput('u_password_confirmation');
                return;
            }

            // 이름 필수
            if (this.getInputValue('u_name') === '') {
                alert('이름을 입력해 주세요.');
                this.focusInput('u_name');
                return;
            }

            // 휴대폰 필수
            if (this.getInputValue('u_cell') === '') {
                alert('휴대폰 번호를 입력해 주세요.');
                this.focusInput('u_cell');
                return;
            }

            // 센터 1곳 이상 필수
            if (this.checkedAgents.length < 1) {
                alert('센터가 선택되지 않았습니다.\n사용을 희망하는 센터를 모두 선택해주세요.');
                return;
            }

            var self = this;
            this.$nextTick(function () {
                self.$el.submit();
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.required-notice {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 12px 16px;
    margin-bottom: 24px;
    background: #fef9f0;
    border: 1px solid #f0e0c0;
    border-radius: 8px;
    font-size: 0.9rem;
    color: #8a7050;
}
.required-notice-icon {
    font-size: 1.2rem;
    color: #d4a850;
}
.required-star {
    color: #f0506e;
    font-weight: bold;
    font-size: 1.1rem;
}

.section-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    background: linear-gradient(135deg, #f8fafc, #eef2f7);
    border-bottom: 1px solid #e5e5e5;
}
.section-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}
.section-icon {
    font-size: 1.6rem;
    color: #555;
}
.section-required-hint {
    font-size: 1rem;
    color: #f0506e;
    font-weight: 500;
}

.slot-hint {
    display: flex;
    align-items: center;
    gap: 4px;
    background: #e0f7f0;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 0.85rem;
    color: #2d8a6e;
    margin-top: 4px;
}
.slot-hint-icon {
    font-size: 1rem;
    color: #2d8a6e;
}

.file-upload-area {
    position: relative;
    border: 2px dashed #d0d0d0;
    border-radius: 12px;
    transition: all 0.2s;

    &:hover {
        border-color: #1e87f0;
        background: #f8fbff;
    }
}
.file-upload-area--has-file {
    border-color: #1e87f0;
    border-style: solid;
    background: #f0f7ff;
}
.file-upload-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
}
.file-upload-content {
    text-align: center;
    padding: 2.5rem 1rem;
}
.file-upload-icon {
    font-size: 3rem;
    color: #b0b0b0;
}
.file-upload-text {
    margin: 0.5rem 0 0;
    color: #999;
    font-size: 0.95rem;
}
.file-upload-text--selected {
    color: #1e87f0;
    font-weight: 500;
}

.agent-card-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.agent-card {
    display: flex;
    align-items: center;
    width: 180px;
    padding: 8px 12px;
    border: 1.5px solid #e5e5e5;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    background: #fff;
    user-select: none;

    &:hover {
        border-color: #a0c8f0;
        background: #f8fbff;
    }
}
.agent-card--selected {
    border-color: #1e87f0;
    background: #f0f7ff;
    box-shadow: 0 1px 4px rgba(30, 135, 240, 0.12);
}
.agent-card-checkbox {
    display: none;
}
.agent-card-check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    min-width: 18px;
    border: 1.5px solid #ccc;
    border-radius: 4px;
    margin-right: 8px;
    transition: all 0.2s;
    background: #fff;
}
.agent-card-check--active {
    border-color: #1e87f0;
    background: #1e87f0;
}
.agent-card-check-icon {
    font-size: 0.85rem;
    color: #fff;
}
.agent-card-flag {
    margin-right: 6px;
}
.agent-card-name {
    font-size: 1rem;
    white-space: nowrap;
}
</style>
